import { defineStore, acceptHMRUpdate } from 'pinia';
import { updateCustomPhrase } from '../lib/callModContent';
import normalizeMarkdown from '../lib/normalizeMarkdown';

interface Translation {
  [name: string]: string;
}

interface Translations {
  [locale: string]: Translation;
}

interface State {
  translations: Translations | null;
}

const twoLineBreaksRegex = /\s*(\\n|\n){2,}\s*/g;
const oneLineBreakRegex = /\s*(\\n|\n)\s*/g;
const forcedLineBreakRegex = /\\+\s*\\n/g;

const unescapeTranslated = (maybeTranslated?: string) => {
  // fix escaped MD bullets: '{r} \*' or '{n} \*'
  maybeTranslated = maybeTranslated?.replace(
    /(?:\{r\}|\{n\})\s*\\\*\s*/g,
    match => {
      return match.replace('\\*', '* ');
    }
  );

  // replace the placeholders back to the original format
  maybeTranslated = maybeTranslated
    ?.replace(/\$\{/g, '@@@@')
    ?.replace(/\\+\s*{n}/g, '{fbr}')
    ?.replace(/{br}/g, '\n\n')
    ?.replace(/{r}/g, '\n\n')
    ?.replace(/{n}/g, '\n')
    ?.replace(/@@@@/g, '${');

  return maybeTranslated;
};

// replace single/double line breaks
// replace escaped forced linebreaks `\\ \n` with a placeholder {fbr} which we replaced after rendering markdown to html
// we use a separate {fbr} placeholder as {br} is used for 2 lines breaks and we need a separate <br> tag for forced linebreaks after parsing to html
const escapeTranslated = (str: string) => {
  return str
    .trim()
    .replace(forcedLineBreakRegex, '{fbr}')
    .replace(twoLineBreaksRegex, ' {r} ')
    .replace(oneLineBreakRegex, ' {n} ');
};

const useTranslations = defineStore({
  id: 'translations',
  state: (): State => ({ translations: null }),

  actions: {
    load(t: Translations) {
      this.translations = t;
    },

    async update({ phrase, to }: { phrase: Phrase; to: string }) {
      const { key } = phrase;
      if (!key) return;
      if (!phrase.translated) return;
      phrase.translated = escapeTranslated(phrase.translated);
      this.translations[to][key] = phrase.translated;

      await updateCustomPhrase({ phrase, to });
    },

    translate(to, key, fallback = key) {
      if (Number.isFinite(fallback)) return String(fallback);

      // get the translation if it exists
      let maybeTranslated =
        this.translations?.[to]?.[key] || this.translations?.[to]?.[fallback];

      // replace linebreaks, using @@@@ to escape the ${} placeholders
      maybeTranslated = unescapeTranslated(maybeTranslated);

      return normalizeMarkdown(maybeTranslated || fallback);
    }
  }
});

export default useTranslations;

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTranslations, import.meta.hot));
}
